import Head from 'next/head';
import React from 'react';
import { DEFAULTS } from '../constants';
import { HeadSEOMetadata } from './types';

/**
 * A component built on top of next/Head to dynamically manage meta tags and SEO content for the webapp
 */
export const HeadSEO: React.FunctionComponent<HeadSEOMetadata> = ({
  title,
  description,
  keywords,
  shouldIndex,
  shouldFollow,
  metaTags,
  imageUrl,
  canonicalUrl,
  iosAppUrl,
  androidAppUrl,
  schemas,
  hrefLangAlternates,
}) => {
  const pageTitle = title ? `${title} | ${DEFAULTS.brandMark}` : '';

  const robotsIndexValue = shouldIndex === false ? 'noindex' : 'index'; // default to index
  const robotsFollowValue = shouldFollow === false ? 'nofollow' : 'follow'; // default to follow

  return (
    <Head>
      {title && <title>{pageTitle}</title>}
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}

      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      <meta name="robots" content={`${robotsIndexValue}, ${robotsFollowValue}`} />

      {/* OG Tags and other meta tags */}
      {metaTags?.map(({ key, value }, index) => {
        return value && (key.startsWith('og:') || key.startsWith('fb:')) ? (
          <meta key={`${key}-${index}`} property={key} content={value} />
        ) : (
          <meta key={`${key}-${index}`} name={key} property={key} content={value} />
        );
      })}

      {/* Hreflang tags */}
      {hrefLangAlternates?.flatMap((alternate, index) => {
        const tags = [];
        if (alternate.hrefLang.toLocaleLowerCase().replace('_', '-') === 'en-us') {
          tags.push(
            <link
              key={`hreflang-x-default-${index}`}
              rel="alternate"
              hrefLang="x-default"
              href={alternate.href}
            />,
          );
        }
        tags.push(
          <link
            key={`hreflang-${alternate.hrefLang}-${index}`}
            rel="alternate"
            hrefLang={alternate.hrefLang.replace('_', '-')}
            href={alternate.href}
          />,
        );
        return tags;
      })}

      {/* JsonLD schemas */}
      {schemas?.map((schema, index) => (
        <script
          key={`schema-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema, (key, value) =>
              (key === 'text' || key === 'headline') && typeof value === 'string'
                ? value.replace(/<\/?[^>]+(>|$)/g, '')
                : value,
            ),
          }}
          suppressHydrationWarning={true}
        />
      ))}

      {/* Twitter Tags */}
      <meta name="twitter:card" content={DEFAULTS.twitterCard.Summary} />
      <meta name="twitter:site" content="@eventbrite" />
      <meta name="twitter:title" content={title} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={imageUrl ? imageUrl : DEFAULTS.openGraphImageUrl} />
      <meta name="twitter:app:name:iphone" content="Eventbrite" />
      {iosAppUrl && <meta name="twitter:app:url:iphone" content={iosAppUrl} />}
      <meta name="twitter:app:id:iphone" content="487922291" />
      <meta name="twitter:app:name:googleplay" content="Eventbrite" />
      {androidAppUrl && <meta name="twitter:app:url:googleplay" content={androidAppUrl} />}
      <meta name="twitter:app:id:googleplay" content="com.eventbrite.attendee" />
    </Head>
  );
};
